import { ChangeEvent, useEffect, useRef, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store";
import {
  getStage4Batch,
  getStage4DownloadUrl,
  getStage4PreviewSheets,
  getStage4SheetPreview,
  reconstructStage4Batch,
  retryStage4BatchJob,
  Stage4Batch,
  Stage4BatchFile,
  Stage4PreviewSheet,
  Stage4SheetPreview,
} from "../../api/companyExcelOnboarding";

const TEMPLATE_URL =
  "https://equion-dev.s3.us-west-2.amazonaws.com/captable-templates/Stage4-Onboarding.xlsx";
const ACTIVE_BATCH_KEY = "stage4ActiveBatchId";

const batchMessage = (batch: Stage4Batch) => {
  if (batch.status === "failed") return "Stage-4 batch failed";
  if (batch.status === "completed_with_errors")
    return `${batch.completedFiles} ready, ${batch.failedFiles} needs retry`;
  if (batch.status === "completed") return "All Stage-4 workbooks are ready";
  if (batch.processingFiles)
    return `Reconstructing ${batch.processingFiles} of ${batch.totalFiles} workbooks`;
  return `${batch.pendingFiles || batch.totalFiles} workbooks queued`;
};

const statusClass = (status: string) =>
  status === "completed"
    ? "bg-green-100 text-green-700"
    : status === "failed"
    ? "bg-red-100 text-red-700"
    : "bg-indigo-100 text-indigo-700";

const statusLabel: Record<string, string> = {
  pending: "Queued",
  processing: "Reconstructing",
  completed: "Ready for review",
  failed: "Failed",
};

export default function ExternalCapTableStage4() {
  const navigate = useNavigate();
  const companyId = useAuthStore((state) => state.user?.companyId);
  // Batches are local UI state. Namespacing them avoids restoring a previous
  // draft company's upload after the user switches companies.
  const activeBatchKey = `${ACTIVE_BATCH_KEY}:${companyId || "unassigned"}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [batch, setBatch] = useState<Stage4Batch | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sheets, setSheets] = useState<Stage4PreviewSheet[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [preview, setPreview] = useState<Stage4SheetPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshBatch = async (batchId: string) => {
    const nextBatch = await getStage4Batch(batchId);
    setBatch(nextBatch);
    return nextBatch;
  };

  useEffect(() => {
    // Do not show the previously selected company's batch while the new
    // company's scoped batch is restored.
    setBatch(null);
    setSelectedJobId(null);
    setSheets([]);
    setSelectedSheet(null);
    setPreview(null);
    setError(null);
    const batchId = sessionStorage.getItem(activeBatchKey);
    if (batchId)
      refreshBatch(batchId).catch(() => {
        sessionStorage.removeItem(activeBatchKey);
        setError("Could not restore the Stage-4 batch.");
      });
  }, [activeBatchKey]);

  useEffect(() => {
    const update = () => {
      const batchId = sessionStorage.getItem(activeBatchKey);
      if (!batchId) return;
      refreshBatch(batchId).catch(() =>
        setError("Could not refresh the Stage-4 batch.")
      );
    };
    window.addEventListener("stage4-batch-updated", update);
    return () => window.removeEventListener("stage4-batch-updated", update);
  }, [activeBatchKey]);

  const selectWorkbook = async (file: Stage4BatchFile) => {
    setSelectedJobId(file.jobId);
    setSheets([]);
    setSelectedSheet(null);
    setPreview(null);
    if (file.status !== "completed") return;
    setLoadingPreview(true);
    try {
      const nextSheets = (await getStage4PreviewSheets(file.jobId)).filter(
        (sheet) => sheet.name.trim().toLowerCase() !== "reconstruction summary"
      );
      setSheets(nextSheets);
      if (nextSheets[0]) {
        setSelectedSheet(nextSheets[0].name);
        setPreview(await getStage4SheetPreview(file.jobId, nextSheets[0].name));
      }
    } catch (_) {
      toast.error("Could not load this workbook preview.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const selectSheet = async (sheet: string) => {
    if (!selectedJobId) return;
    setLoadingPreview(true);
    try {
      const nextPreview = await getStage4SheetPreview(selectedJobId, sheet);
      setSelectedSheet(sheet);
      setPreview(nextPreview);
    } catch (requestError: any) {
      toast.error(
        requestError?.response?.data?.errorMessage ||
          requestError?.response?.data?.error ||
          "Could not load this sheet."
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const start = async () => {
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const nextBatch = await reconstructStage4Batch(files, TEMPLATE_URL);
      sessionStorage.setItem(activeBatchKey, nextBatch.batchId);
      setBatch(nextBatch);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      const firstFile = nextBatch.files[0];
      if (firstFile) setSelectedJobId(firstFile.jobId);
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.errorMessage ||
          "Unable to start the Stage-4 batch."
      );
    } finally {
      setUploading(false);
    }
  };

  const download = async (file: Stage4BatchFile) => {
    try {
      const { downloadUrl } = await getStage4DownloadUrl(file.jobId);
      if (downloadUrl)
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (_) {
      toast.error("Could not prepare the workbook download.");
    }
  };

  const retry = async (file: Stage4BatchFile) => {
    if (!batch) return;
    try {
      setBatch(await retryStage4BatchJob(batch.batchId, file.jobId));
    } catch (_) {
      toast.error(`Could not retry ${file.filename}.`);
    }
  };

  const clearBatch = () => {
    sessionStorage.removeItem(activeBatchKey);
    setBatch(null);
    setSelectedJobId(null);
    setSheets([]);
    setPreview(null);
    setError(null);
  };

  const selectedFile = batch?.files.find(
    (file) => file.jobId === selectedJobId
  );
  const terminal =
    batch &&
    ["completed", "completed_with_errors", "failed"].includes(batch.status);
  const populatedRows = (preview?.rows || []).filter((row) =>
    row.some((value) => String(value ?? "").trim() !== "")
  );
  // Stage-4 templates declare field requirements in a row containing
  // "Required" and "Optional". Use that row for every worksheet instead of
  // treating ordinary optional blanks as missing data.
  const requirementRowIndex = populatedRows.findIndex((row) =>
    row.some(
      (value) =>
        String(value ?? "")
          .trim()
          .toLowerCase() === "required"
    )
  );
  const requiredColumnIndexes = new Set(
    requirementRowIndex < 0
      ? []
      : populatedRows[requirementRowIndex]
          .map((value, index) =>
            String(value ?? "")
              .trim()
              .toLowerCase() === "required"
              ? index
              : -1
          )
          .filter((index) => index >= 0)
  );
  const hasDataRows =
    requirementRowIndex < 0 || populatedRows.length > requirementRowIndex + 1;
  // If reconstruction produced no rows after the requirements row, render one
  // review row so every required field clearly shows as not fetched.
  const displayRows =
    requirementRowIndex >= 0 && !hasDataRows
      ? [
          ...populatedRows,
          Array.from({ length: preview?.columns.length || 0 }, () => null),
        ]
      : populatedRows;
  const visibleColumns = (preview?.columns || [])
    .map((column, index) => ({ column, index }))
    .filter(({ index }) =>
      populatedRows.some((row) => String(row[index] ?? "").trim() !== "")
    );

  return (
    <div className="min-h-screen bg-gray-50 px-3 py-6">
      <div className="mx-auto w-full max-w-[1800px]">
        <button
          type="button"
          onClick={() => navigate("/setting/users")}
          className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to onboarded companies
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">
              External Cap Table → Stage-4
            </h1>
            <p className="mt-2 text-gray-600">
              Each workbook is reconstructed and reviewed independently.
            </p>
          </div>
          {terminal && (
            <button
              type="button"
              onClick={clearBatch}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              Clear batch
            </button>
          )}
        </div>
        {!batch && (
          <section className="mt-8 rounded-xl border border-gray-200 bg-white p-6 text-center">
            <input
              ref={inputRef}
              className="hidden"
              type="file"
              multiple
              accept=".xlsx,.xlsm"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setFiles(Array.from(event.target.files || []))
              }
            />
            <h2 className="text-lg font-medium">Upload external workbooks</h2>
            <p className="mt-2 text-sm text-gray-500">
              Excel files only (.xlsx or .xlsm)
            </p>
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="mt-5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              Choose workbooks
            </button>
            {files.length > 0 && (
              <div className="mx-auto mt-4 max-w-lg space-y-2">
                {files.map((file) => (
                  <p
                    key={`${file.name}-${file.lastModified}`}
                    className="rounded bg-gray-100 px-3 py-2 text-left text-sm"
                  >
                    {file.name}
                  </p>
                ))}
                <button
                  type="button"
                  disabled={uploading}
                  onClick={start}
                  className="mt-3 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {uploading
                    ? "Uploading…"
                    : `Start ${files.length} workbook${
                        files.length === 1 ? "" : "s"
                      }`}
                </button>
              </div>
            )}
          </section>
        )}
        {error && (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}
        {batch && (
          <section className="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b p-5">
              <div>
                <h2 className="font-semibold">Stage-4 batch</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Select a workbook to inspect its own result and preview.
                </p>
              </div>
              <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-700">
                {batchMessage(batch)}
              </span>
            </div>
            <div className="grid min-h-[30rem] lg:grid-cols-[20rem_minmax(0,1fr)]">
              <aside className="border-b bg-slate-50 p-3 lg:border-b-0 lg:border-r">
                {batch.files.map((file) => (
                  <button
                    type="button"
                    key={file.jobId}
                    onClick={() => selectWorkbook(file)}
                    className={`mb-2 w-full rounded-lg border p-3 text-left ${
                      selectedJobId === file.jobId
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-transparent bg-white hover:border-slate-200"
                    }`}
                  >
                    <p className="truncate font-medium text-slate-800">
                      {file.filename}
                    </p>
                    <span
                      className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusClass(
                        file.status
                      )}`}
                    >
                      {statusLabel[file.status] || file.status}
                    </span>
                  </button>
                ))}
              </aside>
              <main className="min-w-0 p-5">
                {!selectedFile && (
                  <p className="text-sm text-gray-500">
                    Select a workbook to view its status and result.
                  </p>
                )}
                {selectedFile && (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {selectedFile.filename}
                        </h3>
                        <p className="mt-1 text-sm capitalize text-slate-500">
                          {statusLabel[selectedFile.status] ||
                            selectedFile.status}
                        </p>
                        {selectedFile.error && (
                          <p className="mt-2 text-sm text-red-700">
                            {selectedFile.error}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {selectedFile.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => download(selectedFile)}
                            className="rounded border px-3 py-2 text-sm"
                          >
                            Download
                          </button>
                        )}
                        {selectedFile.status === "failed" && (
                          <button
                            type="button"
                            onClick={() => retry(selectedFile)}
                            className="rounded border px-3 py-2 text-sm"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                    {selectedFile.status !== "completed" && (
                      <div className="mt-8 rounded-lg bg-slate-50 p-5 text-sm text-slate-600">
                        This workbook’s preview becomes available as soon as
                        reconstruction completes.
                      </div>
                    )}
                    {selectedFile.status === "completed" && (
                      <>
                        <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                          <span className="font-semibold">
                            AI-generated data.
                          </span>{" "}
                          Review all reconstructed values before downloading the
                          workbook. Fields marked <strong>Not fetched</strong>{" "}
                          are required by that worksheet but were not found in
                          the uploaded file, and require your input or
                          confirmation.
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2 border-b pb-4">
                          {sheets.map((sheet) => (
                            <button
                              type="button"
                              key={sheet.name}
                              onClick={() => selectSheet(sheet.name)}
                              className={`rounded px-3 py-2 text-sm ${
                                selectedSheet === sheet.name
                                  ? "bg-indigo-600 text-white"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {sheet.name}
                            </button>
                          ))}
                        </div>
                        {loadingPreview && (
                          <p className="mt-5 text-sm text-slate-500">
                            Loading preview…
                          </p>
                        )}
                        {preview && !loadingPreview && (
                          <div className="mt-5 max-h-[40rem] overflow-auto rounded-lg border">
                            <table className="min-w-full text-left text-sm">
                              <thead className="sticky top-0 bg-slate-100">
                                <tr>
                                  {visibleColumns.map(({ column, index }) => (
                                    <th
                                      key={`${column}-${index}`}
                                      className="whitespace-nowrap px-3 py-2"
                                    >
                                      {column || `Column ${index + 1}`}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {displayRows.map((row, rowIndex) => (
                                  <tr key={rowIndex} className="border-t">
                                    {visibleColumns.map(({ index }) => {
                                      const isMissingRequiredValue =
                                        rowIndex > requirementRowIndex &&
                                        requiredColumnIndexes.has(index) &&
                                        String(row[index] ?? "").trim() === "";
                                      return (
                                        <td
                                          key={index}
                                          title={
                                            isMissingRequiredValue
                                              ? "Not fetched from the uploaded file"
                                              : undefined
                                          }
                                          className={`whitespace-nowrap px-3 py-2 ${
                                            isMissingRequiredValue
                                              ? "bg-blue-50 text-blue-700"
                                              : ""
                                          }`}
                                        >
                                          {isMissingRequiredValue
                                            ? "Not fetched"
                                            : row[index] ?? ""}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </main>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
